import React, { useState, useEffect, memo, useRef } from "react";
import "../ViewEdit/Test.css";
import axiosInst from "../../../Config";
import { BiArrowBack } from "react-icons/bi";

const PatientForm = ({ setIsFormOpened, formData, setFormData, onSuccess, from = "" }) => {
  const [localData, setLocalData] = useState(() => {
    if (!formData || !formData.questions) return formData;

    console.log("Form data:" ,formData);
    
    let parsedAdditionalAnswers = formData.additional_questions_answers;
    if (typeof parsedAdditionalAnswers === 'string') {
        try { parsedAdditionalAnswers = JSON.parse(parsedAdditionalAnswers); } catch(e) { parsedAdditionalAnswers = null; }
    }

    return {
      ...formData,
      questions: formData.questions.map(q => {
        let restoredAnswers = q.additional_questions_answers || q.additional_options || {};
        
        // Handle new structure where answers are in q.additional_answer array
        if (Array.isArray(q.additional_answer)) {
           restoredAnswers = {};
           q.additional_answer.forEach(a => {
               restoredAnswers[a.question_id] = a;
           });
        }

        if (Array.isArray(parsedAdditionalAnswers)) {
           const answersForQ = parsedAdditionalAnswers.filter(a => a.parent_question_id === q.question_id);
           if (answersForQ.length > 0) {
               restoredAnswers = {};
               answersForQ.forEach(a => {
                   restoredAnswers[a.question_id] = a;
               });
           }
        }
        
        return {
          ...q,
          additional_questions_answers: restoredAnswers
        };
      })
    };
  });

  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [historyStack, setHistoryStack] = useState([]);
  const formTitleRef = useRef(null);

  useEffect(() => {
    // Sync localData if needed, but for forms we typically trust the initial load
  }, [formData]);

  if (!localData || !localData.questions) return null;

  const isSaved = localData.isSaved === true;

  const getOpts = (q) => {
    if (!q) return [];
    if (q.options && Array.isArray(q.options)) return q.options;
    const optionsObj =
      localData?.options ||
      localData?.Assessment_options ||
      localData?.assessment_options;
    if (!optionsObj) return [];
    if (typeof optionsObj === "object" && !Array.isArray(optionsObj)) {
      return optionsObj[q.question_id] || [];
    }
    try {
      const parsed = JSON.parse(optionsObj);
      if (typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed[q.question_id] || [];
      }
    } catch (e) { }
    return [];
  };

  const handleSelect = (qId, optionId, isMulti) => {
    if (isSaved) return;

    const updated = {
      ...localData,
      questions: localData.questions.map((q) => {
        if (q.question_id === qId) {
          const opts = getOpts(q);
          const selectedOpt = opts.find((o) => String(o.option_id) === String(optionId));
          const score = selectedOpt?.score || 0;

          if (isMulti) {
            const currentSelected = Array.isArray(
              q.selected_option_id || q.selected_option
            )
              ? q.selected_option_id || q.selected_option
              : (q.selected_option_id || q.selected_option)
                ? [q.selected_option_id || q.selected_option]
                : [];
            const newSelected = currentSelected.includes(optionId)
              ? currentSelected.filter((id) => id !== optionId)
              : [...currentSelected, optionId];
            return {
              ...q,
              selected_option_id: newSelected,
              selected_option: newSelected,
              score: score,
              value: {
                question_id: q.question_id,
                selected_option_id: newSelected,
                selected_option: newSelected,
                score: score,
                additional_questions: q.additional_questions_answers || {},
              },
            };
          } else {
            const currentVal = q.selected_option_id || q.selected_option;
            const newSelected = String(currentVal) === String(optionId) ? null : optionId;
            return {
              ...q,
              selected_option_id: newSelected,
              selected_option: newSelected,
              score: newSelected ? score : 0,
              value: {
                question_id: q.question_id,
                selected_option_id: newSelected,
                selected_option: newSelected,
                score: newSelected ? score : 0,
                additional_questions: q.additional_questions_answers || {},
              },
            };
          }
        }
        return q;
      }),
    };
    setLocalData(updated);
  };

  // [NEW] handleTextChange for type: "text" questions
  const handleTextChange = (qId, textValue) => {
    if (isSaved) return;

    const updated = {
      ...localData,
      questions: localData.questions.map((q) => {
        if (q.question_id === qId) {
          return {
            ...q,
            selected_option_id: textValue,
            selected_option: textValue,
            score: 0,
            value: {
              question_id: q.question_id,
              selected_option_id: textValue,
              selected_option: textValue,
              score: 0,
              additional_questions: q.additional_questions_answers || {},
            },
          };
        }
        return q;
      }),
    };
    setLocalData(updated);
  };

  // [UPDATED] Added opt parameter for single-type additional questions
  const handleAdditionalChange = (qId, addQ, val, opt = null) => {
    if (isSaved) return;
    
    const updated = {
      ...localData,
      questions: localData.questions.map((q) => {
        if (q.question_id === qId) {
          const currentAddMap = q.additional_questions_answers || {};
          const ansData = {
            question_id: addQ.question_id,
            question: addQ.question,
            type: addQ.type,
            value: val,
          };

          if (opt) {
            ansData.selected_option_id = opt.option_id;
            ansData.selected_option = opt.option;
            if (opt.score !== undefined) ansData.score = opt.score;
          }

          const newAddMap = {
            ...currentAddMap,
            [addQ.question_id]: ansData,
          };
          return {
            ...q,
            additional_questions_answers: newAddMap,
            value: {
              question_id: q.question_id,
              selected_option_id: q.selected_option_id || q.selected_option,
              selected_option: q.selected_option_id || q.selected_option,
              score: q.score || 0,
              additional_questions: newAddMap,
            },
          };
        }
        return q;
      }),
    };
    setLocalData(updated);
  };

  // [PRESERVED] handleSave with isSaved, onSuccess, saved_form, pid from localData
  const handleSave = () => {
    const questionsToSave = localData.questions.map((q) => {
      const qOpts = getOpts(q);
      return {
        ...q,
        options: qOpts.map(opt => ({
            option_id: opt.option_id,
            score: opt.score,
        })),
        additional_options: q.additional_questions_answers || {},
      };
    });

    const additional_questions_answers = [];
    localData.questions.forEach((q) => {
      if (q.additional_questions_answers) {
        Object.values(q.additional_questions_answers).forEach((ans) => {
          additional_questions_answers.push({
            parent_question_id: q.question_id,
            ...ans,
          });
        });
      }
    });

    const payload = {
      pid: localData.pid, 
      portalDocsRowId: localData.document_id,
      assess_form: localData.assess_form,
      assessment_name: localData.assessment_name,
      questions: questionsToSave,
      additional_questions_answers: additional_questions_answers,
      saved_form: "portal",
      screening_form_id: localData.screening_form_id || null,
    };

    axiosInst
      .post("/vozo/patient/savePatientDocForm", payload)
      .then((res) => {
        if (res.data && res.data.success === false) {
          console.error("Failed to save form:", res.data.msg);
          return;
        }
        
        setFormData({
            ...localData,
            isSaved: true,
        });

        setIsFormOpened(false);
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        console.error("Error saving form:", err);
      });
  };

  // [UPDATED] allAnswered — now checks text type and additional question completeness
  const allAnswered = localData?.questions?.every((q) => {
    const val = q.selected_option_id !== undefined ? q.selected_option_id : q.selected_option;

    let isMainAnswered = false;
    if (q.type === "multi") {
      isMainAnswered = Array.isArray(val) && val.length > 0;
    } else if (q.type === "text") {
      isMainAnswered = val !== undefined && val !== null && String(val).trim() !== "";
    } else {
      isMainAnswered = val !== undefined && val !== null && val !== "" && val !== 0;
    }

    if (!isMainAnswered) return false;

    // Check additional questions completeness
    const opts = getOpts(q);
    const selectedOpt = opts.find((o) => String(o.option_id) === String(val));
    const selectedText = selectedOpt?.option;
    const addQs = q.additional_questions?.[selectedText];

    if (addQs && Array.isArray(addQs) && addQs.length > 0) {
      for (const addQ of addQs) {
        const ans = q.additional_questions_answers?.[addQ.question_id]?.value;
        if (ans === undefined || ans === null || String(ans).trim() === "") {
          return false;
        }
      }
    }

    return true;
  });

  // [UPDATED] isSkipForm — now covers skip_with_score and conditional_skip_with_score
  const isSkipForm =
    (localData?.question_type === "skip_without_score" ||
      localData?.question_type === "skip" ||
      localData?.question_type === "skip_with_score" ||
      localData?.question_type === "conditional_skip_with_score") &&
    from !== "templates";
  const currentQ = localData?.questions?.[currentQIndex];

  // [NEW] evaluateShowIf — for conditional_skip_with_score
  const evaluateShowIf = (showIf, allQuestions) => {
    if (!showIf) return true;
    if (showIf.operator === "count_if") {
      const { questions: targetQs, score, min_count, max_count } = showIf;
      let count = 0;
      targetQs.forEach((qId) => {
        const q = allQuestions.find((x) => x.question_id === qId);
        if (q && q.score === score) {
          count++;
        }
      });
      if (min_count !== null && min_count !== undefined && count < min_count) return false;
      if (max_count !== null && max_count !== undefined && count > max_count) return false;
      return true;
    }
    return true;
  };

  // [NEW] getNextVisibleQIndex — handles next mapping, default fallback, show_if skipping
  const getNextVisibleQIndex = () => {
    if (!currentQ) return -1;

    const opts = getOpts(currentQ);
    const selectedOptId = currentQ.selected_option_id !== undefined ? currentQ.selected_option_id : currentQ.selected_option;
    const selectedOpt = opts.find((o) => String(o.option_id) === String(selectedOptId));
    const selectedText = selectedOpt?.option;

    let nextQId = undefined;
    if (currentQ.next && selectedText && currentQ.next[selectedText] !== undefined) {
      nextQId = currentQ.next[selectedText];
    } else if (currentQ.next && currentQ.next["default"] !== undefined) {
      nextQId = currentQ.next["default"];
    }

    if (nextQId === null) return -1;

    let targetIndex = -1;
    if (nextQId !== undefined && nextQId !== null) {
      targetIndex = localData.questions.findIndex((q) => q.question_id === nextQId);
    } else {
      targetIndex = currentQIndex + 1;
      if (targetIndex >= localData.questions.length) return -1;
    }

    while (targetIndex !== -1 && targetIndex < localData.questions.length) {
      const targetQ = localData.questions[targetIndex];
      if (targetQ.show_if && !evaluateShowIf(targetQ.show_if, localData.questions)) {
        let skipNextQId = undefined;
        if (targetQ.next) {
          const nextVals = Object.values(targetQ.next).filter((v) => v !== undefined);
          if (nextVals.length > 0) skipNextQId = nextVals[0];
        }

        if (skipNextQId === null) {
          return -1;
        } else if (skipNextQId !== undefined) {
          targetIndex = localData.questions.findIndex((q) => q.question_id === skipNextQId);
        } else {
          targetIndex++;
        }
      } else {
        return targetIndex;
      }
    }
    return -1;
  };

  // [UPDATED] handleContinue — now uses getNextVisibleQIndex
  const handleContinue = () => {
    if (!currentQ) return;
    const targetIndex = getNextVisibleQIndex();

    if (targetIndex !== -1 && targetIndex < localData.questions.length) {
      setHistoryStack((prev) => [...prev, currentQIndex]);
      setCurrentQIndex(targetIndex);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    if (historyStack.length > 0) {
      const prevIndex = historyStack[historyStack.length - 1];
      setHistoryStack((prev) => prev.slice(0, -1));
      setCurrentQIndex(prevIndex);
    }
  };

  // [NEW] isCurrentQAnswered — checks main answer + additional questions
  const isCurrentQAnswered = (() => {
    if (!currentQ) return false;
    const val = currentQ.selected_option_id !== undefined ? currentQ.selected_option_id : currentQ.selected_option;

    let isMainAnswered = false;
    if (currentQ.type === "multi") {
      isMainAnswered = Array.isArray(val) && val.length > 0;
    } else if (currentQ.type === "text") {
      isMainAnswered = val !== undefined && val !== null && String(val).trim() !== "";
    } else {
      isMainAnswered = val !== undefined && val !== null && val !== "";
    }

    if (!isMainAnswered) return false;

    const opts = getOpts(currentQ);
    const selectedOpt = opts.find((o) => String(o.option_id) === String(val));
    const selectedText = selectedOpt?.option;
    const addQs = currentQ.additional_questions?.[selectedText];

    if (addQs && Array.isArray(addQs) && addQs.length > 0) {
      for (const addQ of addQs) {
        const ans = currentQ.additional_questions_answers?.[addQ.question_id]?.value;
        if (ans === undefined || ans === null || String(ans).trim() === "") {
          return false;
        }
      }
    }

    return true;
  })();

  // [UPDATED] progressPercentage — smarter calculation with 100% at end
  const progressPercentage = localData?.questions?.length
    ? getNextVisibleQIndex() === -1 && isCurrentQAnswered
      ? 100
      : Math.min(
        100,
        Math.round(((currentQIndex + (isCurrentQAnswered ? 1 : 0)) / localData.questions.length) * 100)
      )
    : 0;

  return (
    <div className="pf-page">
      {isSkipForm ? (
        <>
          {/* TITLE */}
          <div className="pf-title" ref={formTitleRef}>
            <BiArrowBack
              style={{ cursor: "pointer", marginRight: "10px", verticalAlign: "middle" }}
              onClick={() => setIsFormOpened(false)}
            />
            {localData.assessment_name}
          </div>

          {/* FORM BOX */}
          <div
            className="pf-box"
            style={{
              background: "#fff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              padding: 0,
              minHeight: "240px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div style={{ padding: "24px 28px", flex: 1, display: "flex", flexDirection: "column" }}>
            {/* FORM NAME (ALIGNED LEFT) */}
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1f2937",
                marginBottom: "12px",
                textAlign: "left",
              }}
            >
              {localData?.assess_form}
            </div>

            {/* [UPDATED] DYNAMIC PROGRESS BAR with percentage label */}
            <div
              style={{
                position: "relative",
                marginBottom: "32px",
                marginTop: "24px",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: `min(max(${progressPercentage}%, 15px), calc(100% - 15px))`,
                  transform: "translateX(-50%)",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#1f2937",
                  transition: "left 0.3s ease-in-out",
                }}
              >
                {progressPercentage}%
              </div>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${progressPercentage}%`,
                    backgroundColor: "#5b93ff",
                    borderRadius: "3px",
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </div>
            </div>

            <div className="pf-scroll">
              {currentQ && (
                <div className="pf-q-block" style={{ marginBottom: "10px", textAlign: "left" }}>
                  {/* SECTION SUBHEAD (ALIGNED LEFT) */}
                  {currentQ.section && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginBottom: "6px",
                        fontWeight: "600",
                        textAlign: "left",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <span dangerouslySetInnerHTML={{ __html: currentQ.section }} />
                    </div>
                  )}
                  <div
                    className="pf-q-title"
                    style={{
                      fontWeight: "700",
                      fontSize: "15px",
                      color: "#111827",
                      marginBottom: "6px",
                      textAlign: "left",
                    }}
                  >
                    Question {currentQ.question_no || currentQIndex + 1} :
                  </div>
                  <div
                    className="pf-q-text"
                    style={{
                      fontSize: "14px",
                      color: "#374151",
                      marginBottom: "16px",
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {currentQ.question}
                  </div>

                  {/* [UPDATED] OPTIONS — now supports text type */}
                  {currentQ.type === "text" ? (
                    <div
                      className="pf-options"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="text"
                        value={currentQ.selected_option_id || currentQ.selected_option || ""}
                        onChange={(e) => handleTextChange(currentQ.question_id, e.target.value)}
                        placeholder="Enter details..."
                        readOnly={from === "templates"}
                        disabled={isSaved}
                        style={{
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px",
                          width: "100%",
                          maxWidth: "420px",
                          outline: "none",
                          background: "#ffffff",
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="pf-options"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {getOpts(currentQ).map((opt) => {
                        const isMulti = currentQ.type === "multi";
                        const selVal = currentQ.selected_option_id !== undefined ? currentQ.selected_option_id : currentQ.selected_option;
                        const isChecked = isMulti
                          ? Array.isArray(selVal) && selVal.includes(opt.option_id)
                          : String(selVal) === String(opt.option_id);

                        return (
                          <label
                            key={opt.option_id}
                            className="pf-option-row"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              cursor: isSaved ? "default" : "pointer",
                              fontSize: "14px",
                              color: "#4b5563",
                            }}
                          >
                            <input
                              type={isMulti ? "checkbox" : "radio"}
                              name={`question-${currentQ.question_id}`}
                              checked={from !== "templates" && isChecked}
                              disabled={isSaved}
                              onChange={() =>
                                handleSelect(
                                  currentQ.question_id,
                                  opt.option_id,
                                  isMulti
                                )
                              }
                              style={{
                                cursor: isSaved ? "default" : "pointer",
                                width: "16px",
                                height: "16px",
                                accentColor: "#5b93ff",
                              }}
                            />
                            <span>{opt.option}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* [UPDATED] ADDITIONAL QUESTIONS — now supports single type radio */}
                  {(() => {
                    const opts = getOpts(currentQ);
                    const selVal = currentQ.selected_option_id !== undefined ? currentQ.selected_option_id : currentQ.selected_option;
                    const selectedOpt = opts.find((o) => String(o.option_id) === String(selVal));
                    const selectedText = selectedOpt?.option;
                    const addQs = currentQ.additional_questions?.[selectedText];

                    if (!addQs || !Array.isArray(addQs) || addQs.length === 0)
                      return null;

                    return (
                      <div
                        style={{
                          marginTop: "24px",
                          marginBottom: "16px",
                          padding: "18px 20px 28px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          display: "flex",
                          flexDirection: "column",
                          gap: "16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#4b5563",
                            borderBottom: "1px solid #e5e7eb",
                            paddingBottom: "8px",
                          }}
                        >
                          Additional Details Required
                        </div>

                        {addQs.map((addQ) => {
                          const currentAns =
                            currentQ.additional_questions_answers?.[addQ.question_id]?.value || "";

                          return (
                            <div
                              key={addQ.question_id}
                              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
                            >
                              <label
                                style={{
                                  fontSize: "13.5px",
                                  fontWeight: "500",
                                  color: "#374151",
                                }}
                              >
                                {addQ.question}
                              </label>

                              {addQ.type === "time picker" ? (
                                <input
                                  type="time"
                                  value={currentAns}
                                  disabled={isSaved}
                                  onChange={(e) =>
                                    handleAdditionalChange(
                                      currentQ.question_id,
                                      addQ,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "14px",
                                    outline: "none",
                                    background: "#ffffff",
                                    width: "180px",
                                  }}
                                />
                              ) : addQ.type === "number" ? (
                                <input
                                  type="number"
                                  value={currentAns}
                                  placeholder="Enter number..."
                                  disabled={isSaved}
                                  onChange={(e) =>
                                    handleAdditionalChange(
                                      currentQ.question_id,
                                      addQ,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "14px",
                                    width: "180px",
                                    outline: "none",
                                    background: "#ffffff",
                                  }}
                                />
                              ) : addQ.type === "single" && Array.isArray(addQ.options) ? (
                                /* [NEW] single-type radio additional questions */
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                                  {addQ.options.map((opt) => (
                                    <label
                                      key={opt.option_id}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        cursor: isSaved ? "default" : "pointer",
                                        fontSize: "14px",
                                        color: "#4b5563",
                                      }}
                                    >
                                      <input
                                        type="radio"
                                        name={`addQ-${currentQ.question_id}-${addQ.question_id}`}
                                        checked={String(currentAns) === String(opt.option_id)}
                                        disabled={isSaved}
                                        onChange={() =>
                                          handleAdditionalChange(
                                            currentQ.question_id,
                                            addQ,
                                            opt.option_id,
                                            opt
                                          )
                                        }
                                        style={{
                                          cursor: isSaved ? "default" : "pointer",
                                          width: "16px",
                                          height: "16px",
                                          accentColor: "#5b93ff",
                                        }}
                                      />
                                      <span>{opt.option}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={currentAns}
                                  placeholder="Enter details..."
                                  disabled={isSaved}
                                  onChange={(e) =>
                                    handleAdditionalChange(
                                      currentQ.question_id,
                                      addQ,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "14px",
                                    width: "100%",
                                    maxWidth: "420px",
                                    outline: "none",
                                    background: "#ffffff",
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
              </div>
            )}
            
            {localData?.footers && currentQIndex === 0 && (
              <div style={{ flex: 1, minHeight: "60px" }}></div>
            )}
            </div>
            
            {localData?.footers && currentQIndex === 0 && (
              <div
                className="scoreboard-footer-content"
                style={{
                  padding: "16px 28px",
                  fontSize: "13px",
                  color: "#444",
                  borderTop: "1px solid #e5e7eb",
                }}
                dangerouslySetInnerHTML={{ __html: localData.footers }}
              />
            )}
          </div>
          </div>

          {/* NAVIGATION BUTTONS */}
          {from !== "templates" && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={handleBack}
                  disabled={historyStack.length === 0}
                  style={{
                    padding: "8px 24px",
                    borderRadius: "6px",
                    border: "1.5px solid #7aa7ff",
                    background: "#ffffff",
                    color: historyStack.length === 0 ? "#9ca3af" : "#5b93ff",
                    borderColor:
                      historyStack.length === 0 ? "#d1d5db" : "#7aa7ff",
                    cursor:
                      historyStack.length === 0 ? "not-allowed" : "pointer",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Back
                </button>

                {/* [UPDATED] Continue/Save button — uses isCurrentQAnswered and getNextVisibleQIndex */}
                {(!isSaved || getNextVisibleQIndex() !== -1) && (
                  <button
                    onClick={handleContinue}
                    disabled={!isSaved && !isCurrentQAnswered}
                    style={{
                      padding: "8px 24px",
                      borderRadius: "6px",
                      border: "none",
                      background: isSaved || isCurrentQAnswered ? "#2C7BE5" : "#e5e7eb",
                      color: isSaved || isCurrentQAnswered ? "#ffffff" : "#9ca3af",
                      cursor: isSaved || isCurrentQAnswered ? "pointer" : "not-allowed",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    {getNextVisibleQIndex() === -1 ? "Save" : "Continue"}
                  </button>
                )}
              </div>

              <button
                onClick={() => setIsFormOpened(false)}
                style={{
                  padding: "8px 24px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#cccccc",
                  color: "#333333",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* [UPDATED] TITLE — now with back arrow */}
          <div className="pf-title" ref={formTitleRef}>
            <BiArrowBack
              style={{ cursor: "pointer", marginRight: "10px", verticalAlign: "middle" }}
              onClick={() => setIsFormOpened(false)}
            />
            {localData.assessment_name}
          </div>

          {/* FORM BOX */}
          <div className="pf-box" style={{ padding: 0 }}>
            <div className="pf-inner-title" style={{ padding: "20px 20px 0 20px", margin: 0 }}> {localData?.assess_form}</div>

            <div className="pf-scroll" style={{ paddingRight: 0 }}>
              <div style={{ padding: "0 20px" }}>
              {/* [UPDATED] Normal mode — now with sections, question_no, text type */}
              {localData.questions.map((q, index) => {
                const isNewSection = index === 0 || q.section !== localData.questions[index - 1].section;

                return (
                  <div key={q.question_id} className="pf-q-block">
                    {q.section && isNewSection && (
                      <div
                        style={{
                          fontSize: "16px",
                          color: "#1f2937",
                          marginBottom: "8px",
                          fontWeight: "600",
                          textAlign: "left",
                        }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: q.section }} />
                      </div>
                    )}
                    <div className="pf-q-title">Question {q.question_no || index + 1} :</div>

                    <div className="pf-q-text">{q.question}</div>

                    <div className="pf-options">
                      {q.type === "text" ? (
                        <input
                          type="text"
                          value={q.selected_option_id || q.selected_option || ""}
                          onChange={(e) => handleTextChange(q.question_id, e.target.value)}
                          placeholder="Enter details..."
                          readOnly={from === "templates"}
                          disabled={isSaved}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "14px",
                            width: "100%",
                            maxWidth: "420px",
                            outline: "none",
                            background: "#ffffff",
                          }}
                        />
                      ) : (() => {
                        const opts = getOpts(q);
                        const isMulti = q.type === "multi";
                        return opts.map((opt) => {
                          const selVal = q.selected_option_id !== undefined ? q.selected_option_id : q.selected_option;
                          const isChecked = isMulti
                            ? Array.isArray(selVal) &&
                            selVal.includes(opt.option_id)
                            : String(selVal) === String(opt.option_id);

                          return (
                            <label
                              key={opt.option_id}
                              className="pf-option-row"
                            >
                              <input
                                type={isMulti ? "checkbox" : "radio"}
                                checked={from !== "templates" && isChecked}
                                disabled={isSaved}
                                onChange={() =>
                                  handleSelect(
                                    q.question_id,
                                    opt.option_id,
                                    isMulti,
                                  )
                                }
                                className="pf-checkbox"
                              />
                              <span>{opt.option}</span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>
                );
              })}
              </div>
              {localData?.footers && (
                <div
                  className="scoreboard-footer-content"
                  style={{
                    padding: "16px 20px",
                    fontSize: "13px",
                    color: "#444",
                    borderTop: "1px solid #e5e7eb",
                  }}
                  dangerouslySetInnerHTML={{ __html: localData.footers }}
                />
              )}
            </div>
          </div>
          {from !== "templates" && (
            <>
              {/* FOOTER */}
              <div className="pf-footer">
                <button
                  className="pf-cancel"
                  onClick={() => setIsFormOpened(false)}
                >
                  Close
                </button>

                {!isSaved && (
                  <button
                    className="pf-save"
                    onClick={handleSave}
                    disabled={!allAnswered}
                    style={{
                      opacity: allAnswered ? 1 : 0.5,
                      cursor: allAnswered ? "pointer" : "not-allowed",
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default memo(PatientForm);
